export type ProcessAssistantMode =
  | 'draw-flowchart'
  | 'draft-from-notes'
  | 'process-library'
  | 'review-imports'
  | 'improve-process'
  | 'management-summary'
  | 'compliance'
  | 'qms'
  | 'audit-review'
  | 'custom-view';

export type ProcessMaturity =
  | 'empty_quick_flowchart'
  | 'manual_map'
  | 'notes_ready'
  | 'import_review_pending'
  | 'documented_empty'
  | 'repository_seeded'
  | 'repository_mature'
  | 'improvement_ready'
  | 'compliance_active'
  | 'grc_federated'
  | 'viewer_mode';

export type ProcessLayer = 'diagram' | 'repository' | 'compliance' | 'assurance';

export type ProcessSurfacePriority =
  | 'primary'
  | 'contextual'
  | 'tucked'
  | 'absent';

export type ProcessPermission = 'editor' | 'viewer';

export type ProcessSelectedElementKind =
  | 'none'
  | 'step'
  | 'decision'
  | 'lane'
  | 'edge'
  | 'multiple';

export type ProcessAssistantRoot =
  | { kind: 'flowchart-starter' }
  | { kind: 'map-view' }
  | { kind: 'draft-from-notes' }
  | { kind: 'process-library' }
  | { kind: 'review-imports' }
  | { kind: 'improve-process' }
  | { kind: 'management-summary' }
  | { kind: 'compliance' }
  | { kind: 'qms' }
  | { kind: 'audit-review' }
  | { kind: 'custom-view' }
  | { kind: 'view-process' };

export type ProcessChatSuggestionId =
  | 'describe-process'
  | 'draft-from-notes'
  | 'suggest-decision-branches'
  | 'find-bottlenecks'
  | 'check-compliance'
  | 'find-ownership-gaps'
  | 'changed-recently'
  | 'summarize-changes'
  | 'find-evidence-gaps'
  | 'find-decision-points'
  | 'find-risky-changes'
  | 'find-risky-handoffs'
  | 'create-management-brief'
  | 'list-owner-actions'
  | 'check-qms-readiness'
  | 'find-procedure-gaps'
  | 'prepare-audit-walkthrough'
  | 'suggest-useful-view'
  | 'run-structure-scan'
  | 'find-missing-process-data';

export interface ProcessSurfacePlacementInput {
  processPurpose?: string | null;
  processLayer?: ProcessLayer | null;
  folderMaturity?: ProcessMaturity | null;
  nodeCount: number;
  decisionCount: number;
  laneCount: number;
  hasRepositoryRecords: boolean;
  hasSource: boolean;
  pendingReviewCount: number;
  tracksCompliance: boolean;
  hasComplianceLayer: boolean;
  hasImprovementData?: boolean;
  frameworkId?: string | null;
  isFederated: boolean;
  /** Transitional rollout guard for Process-specific repository controls. */
  processRepositoryAvailable?: boolean;
  permission: ProcessPermission;
  selectedElementKind: ProcessSelectedElementKind;
  requestedMode?: ProcessAssistantMode | null;
  hasChatMessages?: boolean;
}

export interface ProcessSurfacePlacement {
  root: ProcessAssistantRoot;
  maturity: ProcessMaturity;
  activeMode: ProcessAssistantMode;
  availableModes: readonly ProcessAssistantMode[];
  headerTitle: string;
  headerSummary: string;
  disabledReason?: string;
  drawFlowchart: ProcessSurfacePriority;
  draftFromNotes: ProcessSurfacePriority;
  processLibrary: ProcessSurfacePriority;
  reviewImports: ProcessSurfacePriority;
  improveProcess: ProcessSurfacePriority;
  managementSummary: ProcessSurfacePriority;
  compliance: ProcessSurfacePriority;
  qms: ProcessSurfacePriority;
  auditReview: ProcessSurfacePriority;
  customView: ProcessSurfacePriority;
  federationStatus: ProcessSurfacePriority;
  viewBox: ProcessSurfacePriority;
  chat: ProcessSurfacePriority;
  quickActions: ProcessSurfacePriority;
  sourcesIndicator: ProcessSurfacePriority;
  chatSuggestionIds: readonly ProcessChatSuggestionId[];
}

const MODE_LABELS: Record<ProcessAssistantMode, string> = {
  'draw-flowchart': 'Draw flowchart',
  'draft-from-notes': 'Draft from notes',
  'process-library': 'Process repository',
  'review-imports': 'Review imports',
  'improve-process': 'Improve process',
  'management-summary': 'Management summary',
  compliance: 'Compliance',
  qms: 'QMS',
  'audit-review': 'Audit review',
  'custom-view': 'Custom view',
};

const hasDiagramStructure = (input: ProcessSurfacePlacementInput): boolean =>
  input.nodeCount > 0 || input.decisionCount > 0 || input.laneCount > 0;

const hasRepositorySignal = (input: ProcessSurfacePlacementInput): boolean =>
  input.hasRepositoryRecords || input.hasSource;

const isProcessRepositoryAvailable = (input: ProcessSurfacePlacementInput): boolean =>
  input.processRepositoryAvailable !== false;

const isDocumentedProcessPurpose = (purpose?: string | null): boolean =>
  purpose === 'documented_process' ||
  purpose === 'audit_ready_compliance' ||
  purpose === 'grc_compass';

const hasComplianceSignal = (input: ProcessSurfacePlacementInput): boolean =>
  input.processPurpose === 'audit_ready_compliance' ||
  input.processPurpose === 'grc_compass' ||
  input.processLayer === 'compliance' ||
  input.processLayer === 'assurance' ||
  input.tracksCompliance ||
  input.hasComplianceLayer ||
  Boolean(input.frameworkId);

const canUseMode = (
  mode: ProcessAssistantMode,
  input: ProcessSurfacePlacementInput,
): boolean => {
  const canWrite = input.permission === 'editor';
  const hasStructure = hasDiagramStructure(input);
  const repositorySignal = hasRepositorySignal(input);
  const complianceSignal = hasComplianceSignal(input);

  switch (mode) {
    case 'draw-flowchart':
    case 'draft-from-notes':
      return canWrite;
    case 'process-library':
      if (!isProcessRepositoryAvailable(input)) return false;
      if (canWrite) return true;
      return (
        repositorySignal ||
        isDocumentedProcessPurpose(input.processPurpose) ||
        input.requestedMode === 'process-library'
      );
    case 'review-imports':
      if (!isProcessRepositoryAvailable(input)) return false;
      return (
        input.pendingReviewCount > 0 ||
        input.hasSource ||
        input.requestedMode === 'review-imports'
      );
    case 'improve-process':
      if (canWrite) return true;
      return hasStructure || input.requestedMode === 'improve-process';
    case 'management-summary':
      if (canWrite) return true;
      return (
        hasStructure ||
        repositorySignal ||
        complianceSignal ||
        input.requestedMode === 'management-summary'
      );
    case 'compliance':
      if (canWrite) return true;
      return complianceSignal || input.requestedMode === 'compliance';
    case 'qms':
      if (canWrite) return true;
      return complianceSignal || input.requestedMode === 'qms';
    case 'audit-review':
      if (canWrite) return true;
      return complianceSignal || input.requestedMode === 'audit-review';
    case 'custom-view':
      return true;
  }
};

const inferMaturity = (
  input: ProcessSurfacePlacementInput,
): ProcessMaturity => {
  if (input.permission === 'viewer') return 'viewer_mode';

  if (input.pendingReviewCount > 0) return 'import_review_pending';

  if (hasComplianceSignal(input)) return 'compliance_active';

  if (input.isFederated) return 'grc_federated';

  if (input.folderMaturity) return input.folderMaturity;

  if (input.hasRepositoryRecords) {
    return input.nodeCount >= 6 || input.decisionCount > 1 || input.laneCount > 0
      ? 'repository_mature'
      : 'repository_seeded';
  }

  if (input.hasSource) return 'notes_ready';

  if (isDocumentedProcessPurpose(input.processPurpose)) {
    return 'documented_empty';
  }

  return hasDiagramStructure(input) ? 'manual_map' : 'empty_quick_flowchart';
};

const inferActiveMode = (
  input: ProcessSurfacePlacementInput,
  maturity: ProcessMaturity,
): ProcessAssistantMode => {
  if (input.requestedMode && canUseMode(input.requestedMode, input)) {
    return input.requestedMode;
  }

  if (input.permission === 'viewer') {
    if (hasComplianceSignal(input)) return 'compliance';
    if (
      isProcessRepositoryAvailable(input) &&
      (hasRepositorySignal(input) || input.isFederated)
    ) return 'process-library';
    return 'custom-view';
  }

  switch (maturity) {
    case 'import_review_pending':
      return isProcessRepositoryAvailable(input) ? 'review-imports' : 'draw-flowchart';
    case 'compliance_active':
      return 'compliance';
    case 'documented_empty':
    case 'notes_ready':
      return 'draft-from-notes';
    case 'repository_seeded':
    case 'repository_mature':
    case 'grc_federated':
      return isProcessRepositoryAvailable(input) ? 'process-library' : 'draw-flowchart';
    case 'improvement_ready':
      return hasDiagramStructure(input) ? 'improve-process' : 'draw-flowchart';
    case 'manual_map':
    case 'empty_quick_flowchart':
    case 'viewer_mode':
      return 'draw-flowchart';
  }
};

const availableModesFor = (
  input: ProcessSurfacePlacementInput,
  activeMode: ProcessAssistantMode,
): readonly ProcessAssistantMode[] => {
  const candidates: ProcessAssistantMode[] =
    input.permission === 'viewer'
      ? [
          'process-library',
          'management-summary',
          'compliance',
          'qms',
          'audit-review',
          'custom-view',
        ]
      : [
          'draw-flowchart',
          'draft-from-notes',
          'process-library',
          'improve-process',
          'management-summary',
          'compliance',
          'qms',
          'audit-review',
          'custom-view',
        ];

  if (
    input.pendingReviewCount > 0 ||
    input.hasSource ||
    input.requestedMode === 'review-imports'
  ) {
    candidates.splice(input.permission === 'viewer' ? 0 : 2, 0, 'review-imports');
  }

  const available = candidates.filter((mode) => canUseMode(mode, input));
  if (!available.includes(activeMode)) {
    available.unshift(activeMode);
  }
  return Array.from(new Set(available));
};

const rootForMode = (
  mode: ProcessAssistantMode,
  input: ProcessSurfacePlacementInput,
): ProcessAssistantRoot => {
  if (input.permission === 'viewer' && mode === 'custom-view') {
    return { kind: 'view-process' };
  }

  switch (mode) {
    case 'draw-flowchart':
      return hasDiagramStructure(input)
        ? { kind: 'map-view' }
        : { kind: 'flowchart-starter' };
    case 'draft-from-notes':
      return { kind: 'draft-from-notes' };
    case 'process-library':
      return { kind: 'process-library' };
    case 'review-imports':
      return { kind: 'review-imports' };
    case 'improve-process':
      return { kind: 'improve-process' };
    case 'management-summary':
      return { kind: 'management-summary' };
    case 'compliance':
      return { kind: 'compliance' };
    case 'qms':
      return { kind: 'qms' };
    case 'audit-review':
      return { kind: 'audit-review' };
    case 'custom-view':
      return { kind: 'custom-view' };
  }
};

const headerSummaryFor = (
  input: ProcessSurfacePlacementInput,
  maturity: ProcessMaturity,
  activeMode: ProcessAssistantMode,
): string => {
  const stepLabel = input.nodeCount === 1 ? '1 step' : `${input.nodeCount} steps`;
  const permissionLabel = input.permission === 'viewer' ? 'view only' : 'editable';

  if (maturity === 'viewer_mode') {
    return `${stepLabel} - ${permissionLabel}`;
  }

  if (activeMode === 'review-imports') {
    const changeLabel =
      input.pendingReviewCount === 1
        ? '1 change to review'
        : `${input.pendingReviewCount} changes to review`;
    return changeLabel;
  }

  if (activeMode === 'compliance') {
    return input.frameworkId
      ? `${input.frameworkId} framework`
      : 'controls and evidence';
  }

  if (activeMode === 'qms') {
    return 'procedure quality';
  }

  if (activeMode === 'audit-review') {
    return 'audit walkthrough';
  }

  if (activeMode === 'process-library') {
    if (input.isFederated) return 'linked process folder';
    return input.hasRepositoryRecords ? `${stepLabel} - repository` : 'repository ready';
  }

  if (activeMode === 'draft-from-notes') {
    return input.hasSource ? 'source linked' : 'paste or import';
  }

  if (activeMode === 'improve-process') {
    return input.hasImprovementData
      ? `${stepLabel} - improvement ready`
      : `${stepLabel} - structure only`;
  }

  if (activeMode === 'management-summary') {
    return `${stepLabel} - management`;
  }

  return input.nodeCount === 0
    ? `blank - ${permissionLabel}`
    : `${stepLabel} - ${permissionLabel}`;
};

const chatSuggestionsFor = (
  activeMode: ProcessAssistantMode,
  input: ProcessSurfacePlacementInput,
): readonly ProcessChatSuggestionId[] => {
  switch (activeMode) {
    case 'draw-flowchart':
      return input.nodeCount === 0
        ? ['describe-process', 'draft-from-notes']
        : ['suggest-decision-branches', 'find-bottlenecks'];
    case 'draft-from-notes':
      return ['draft-from-notes', 'find-decision-points'];
    case 'review-imports':
      return ['summarize-changes', 'find-risky-changes'];
    case 'process-library':
      return ['find-ownership-gaps', 'changed-recently'];
    case 'improve-process':
      if (!input.hasImprovementData) {
        return ['run-structure-scan', 'find-missing-process-data'];
      }
      return ['find-bottlenecks', 'find-risky-handoffs'];
    case 'management-summary':
      return ['create-management-brief', 'list-owner-actions'];
    case 'compliance':
      return ['check-compliance', 'find-evidence-gaps'];
    case 'qms':
      return ['check-qms-readiness', 'find-procedure-gaps'];
    case 'audit-review':
      return ['prepare-audit-walkthrough', 'find-evidence-gaps'];
    case 'custom-view':
      return ['suggest-useful-view', 'find-bottlenecks'];
  }
};

const placementFor = (
  activeMode: ProcessAssistantMode,
  input: ProcessSurfacePlacementInput,
): Pick<
  ProcessSurfacePlacement,
  | 'drawFlowchart'
  | 'draftFromNotes'
  | 'processLibrary'
  | 'reviewImports'
  | 'improveProcess'
  | 'managementSummary'
  | 'compliance'
  | 'qms'
  | 'auditReview'
  | 'customView'
  | 'federationStatus'
  | 'viewBox'
  | 'chat'
  | 'quickActions'
  | 'sourcesIndicator'
> => {
  const canWrite = input.permission === 'editor';
  const hasStructure = hasDiagramStructure(input);
  const repositorySignal = hasRepositorySignal(input);
  const complianceSignal = hasComplianceSignal(input);

  return {
    drawFlowchart: activeMode === 'draw-flowchart'
      ? 'primary'
      : canWrite
        ? 'tucked'
        : 'absent',
    draftFromNotes: activeMode === 'draft-from-notes'
      ? 'primary'
      : canWrite
        ? 'contextual'
        : 'absent',
    processLibrary: !isProcessRepositoryAvailable(input)
      ? 'absent'
      : activeMode === 'process-library'
        ? 'primary'
        : repositorySignal
          ? 'contextual'
          : 'tucked',
    reviewImports: !isProcessRepositoryAvailable(input)
      ? 'absent'
      : activeMode === 'review-imports'
        ? 'primary'
        : input.pendingReviewCount > 0
          ? 'contextual'
          : input.hasSource
            ? 'tucked'
            : 'absent',
    improveProcess: activeMode === 'improve-process'
      ? 'primary'
      : hasStructure
        ? 'contextual'
        : 'tucked',
    managementSummary: activeMode === 'management-summary'
      ? 'primary'
      : hasStructure || repositorySignal
        ? 'contextual'
        : 'tucked',
    compliance: activeMode === 'compliance'
      ? 'primary'
      : complianceSignal || input.requestedMode === 'compliance'
        ? 'contextual'
        : 'tucked',
    qms: activeMode === 'qms'
      ? 'primary'
      : complianceSignal || input.requestedMode === 'qms'
        ? 'contextual'
        : 'tucked',
    auditReview: activeMode === 'audit-review'
      ? 'primary'
      : complianceSignal || input.requestedMode === 'audit-review'
        ? 'contextual'
        : 'tucked',
    customView: activeMode === 'custom-view'
      ? 'primary'
      : hasStructure
        ? 'contextual'
        : 'tucked',
    federationStatus: input.isFederated ? 'contextual' : 'absent',
    viewBox: activeMode === 'custom-view'
      ? 'primary'
      : hasStructure
        ? 'contextual'
        : 'tucked',
    chat: input.hasChatMessages ? 'primary' : 'contextual',
    quickActions: canWrite ? 'tucked' : 'absent',
    sourcesIndicator: repositorySignal || input.pendingReviewCount > 0
      ? 'contextual'
      : 'absent',
  };
};

export const resolveProcessSurfacePlacement = (
  input: ProcessSurfacePlacementInput,
): ProcessSurfacePlacement => {
  const maturity = inferMaturity(input);
  const activeMode = inferActiveMode(input, maturity);
  const availableModes = availableModesFor(input, activeMode);
  const root = rootForMode(activeMode, input);

  return {
    root,
    maturity,
    activeMode,
    availableModes,
    headerTitle:
      input.permission === 'viewer' && root.kind === 'view-process'
        ? 'View process'
        : MODE_LABELS[activeMode],
    headerSummary: headerSummaryFor(input, maturity, activeMode),
    disabledReason:
      input.permission === 'viewer' ? 'You can view this process but cannot make changes.' : undefined,
    ...placementFor(activeMode, input),
    chatSuggestionIds: chatSuggestionsFor(activeMode, input),
  };
};
